// Worker-safe HTML sanitizer for trusted-source article content.
// Uses the `sanitize-html` npm library (already a project dependency, Worker-
// compatible) to avoid known regex bypasses (entity-encoded `javascript:` URIs,
// unquoted `data:` hrefs, the `<base>` tag, etc.).

import sanitizeHtmlLib from "sanitize-html";

const ALLOWED_TAGS = [
  ...sanitizeHtmlLib.defaults.allowedTags.filter(
    (t) => t !== "script" && t !== "style" && t !== "iframe" && t !== "form",
  ),
  "img",
  "figure",
  "figcaption",
  "h1",
  "h2",
];

// Explicitly forbid `base` (can rewrite all relative URLs on the page) and
// other dangerous embedding tags. sanitize-html drops anything not in the
// allowlist, but we also remove disallowed tags' contents for these.
const DISALLOWED_WITH_CONTENT = [
  "script",
  "style",
  "iframe",
  "object",
  "embed",
  "form",
  "noscript",
  "link",
  "meta",
  "svg",
  "math",
  "base",
];

export function sanitizeHtml(input: string): string {
  if (!input) return "";

  return sanitizeHtmlLib(input, {
    allowedTags: ALLOWED_TAGS,
    allowedAttributes: {
      ...sanitizeHtmlLib.defaults.allowedAttributes,
      a: ["href", "name", "target", "rel", "title"],
      img: ["src", "alt", "title", "width", "height", "loading", "srcset", "sizes"],
      "*": ["class", "id", "style"],
    },
    allowedSchemes: ["http", "https", "mailto", "tel"],
    allowedSchemesByTag: {
      img: ["http", "https"],
    },
    allowProtocolRelative: false,
    disallowedTagsMode: "discard",
    nonTextTags: DISALLOWED_WITH_CONTENT,
    transformTags: {
      a: (tagName, attribs) => ({
        tagName,
        attribs: {
          ...attribs,
          rel: attribs.target === "_blank" ? "noopener noreferrer" : (attribs.rel ?? ""),
        },
      }),
    },
  });
}
